/**
 * SurveyEditor - Edit survey questions
 *
 * Features:
 * - Drag & drop question reordering
 * - Edit question text inline
 * - Add/remove questions
 * - Change question type
 * - Edit options for choice questions
 * - Preview mode
 *
 * Access: Survey owner or admin only
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getSurvey, updateSurvey } from '../../services/api';
import { SurveyTemplate, Question } from '../../types';

// Icons
const LogoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-8 h-8">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
  </svg>
);

const SaveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/>
    <polyline points="7 3 7 8 15 8"/>
  </svg>
);

const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <polyline points="3,6 5,6 21,6"/>
    <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2v2"/>
  </svg>
);

const GripIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <circle cx="9" cy="6" r="1"/>
    <circle cx="15" cy="6" r="1"/>
    <circle cx="9" cy="12" r="1"/>
    <circle cx="15" cy="12" r="1"/>
    <circle cx="9" cy="18" r="1"/>
    <circle cx="15" cy="18" r="1"/>
  </svg>
);

const ChevronUpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <polyline points="18 15 12 9 6 15"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

// Question type options
const questionTypes = [
  { value: 'likert', label: 'Škála 1-5 (Likert)' },
  { value: 'yes_no', label: 'Ano / Ne' },
  { value: 'choice', label: 'Výběr z možností' },
  { value: 'open', label: 'Otevřená odpověď' },
];

// Section options
const sectionOptions = [
  { value: 'filtering', label: '1. Filtrační otázky' },
  { value: 'satisfaction', label: '2. Spokojenost' },
  { value: 'priorities', label: '3. Priority' },
  { value: 'values', label: '4. Postoje a hodnoty' },
  { value: 'trust', label: '5. Důvěra' },
  { value: 'communication', label: '6. Komunikace' },
  { value: 'open_questions', label: '7. Otevřené otázky' },
  { value: 'demographics', label: '8. Demografie' },
];

const SurveyEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  // State
  const [survey, setSurvey] = useState<SurveyTemplate | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);

  // Load survey
  useEffect(() => {
    const loadSurvey = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        const data = await getSurvey(id);
        setSurvey(data);
        setQuestions(data.questions || []);
        setTitle(data.title);
        setDescription(data.description);
        setIsActive(data.is_active);
      } catch (err: any) {
        console.error('Failed to load survey:', err);
        setError(err.message || 'Nepodařilo se načíst dotazník');
      } finally {
        setIsLoading(false);
      }
    };

    loadSurvey();
  }, [id]);

  // Track changes
  useEffect(() => {
    if (!survey) return;

    const hasQuestionsChanged = JSON.stringify(questions) !== JSON.stringify(survey.questions);
    const hasTitleChanged = title !== survey.title;
    const hasDescriptionChanged = description !== survey.description;
    const hasActiveChanged = isActive !== survey.is_active;

    setHasChanges(hasQuestionsChanged || hasTitleChanged || hasDescriptionChanged || hasActiveChanged);
  }, [questions, title, description, isActive, survey]);

  // Save changes
  const handleSave = async () => {
    if (!id || !user?.uid) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await updateSurvey(id, {
        title,
        description,
        questions,
        is_active: isActive,
      });

      setSuccessMessage('Dotazník byl úspěšně uložen!');
      setHasChanges(false);

      // Update local survey state
      setSurvey(prev => prev ? {
        ...prev,
        title,
        description,
        questions,
        is_active: isActive,
      } : null);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Failed to save survey:', err);
      setError(err.message || 'Nepodařilo se uložit dotazník');
    } finally {
      setIsSaving(false);
    }
  };

  // Question operations
  const updateQuestion = useCallback((index: number, updates: Partial<Question>) => {
    setQuestions(prev => {
      const newQuestions = [...prev];
      newQuestions[index] = { ...newQuestions[index], ...updates };
      return newQuestions;
    });
  }, []);

  const deleteQuestion = useCallback((index: number) => {
    if (!window.confirm('Opravdu chcete smazat tuto otázku?')) return;
    setQuestions(prev => prev.filter((_, i) => i !== index));
  }, []);

  const addQuestion = useCallback(() => {
    const newQuestion: Question = {
      id: `q${Date.now()}`,
      text: 'Nová otázka',
      type: 'likert',
      section: 'satisfaction',
      required: true,
    };
    setQuestions(prev => [...prev, newQuestion]);
    setExpandedQuestion(newQuestion.id);
  }, []);

  const moveQuestion = useCallback((fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= questions.length) return;

    setQuestions(prev => {
      const newQuestions = [...prev];
      const [removed] = newQuestions.splice(fromIndex, 1);
      newQuestions.splice(toIndex, 0, removed);
      return newQuestions;
    });
  }, [questions.length]);

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    moveQuestion(draggedIndex, index);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Option management for choice questions
  const updateOption = (questionIndex: number, optionIndex: number, value: string) => {
    setQuestions(prev => {
      const newQuestions = [...prev];
      const options = [...(newQuestions[questionIndex].options || [])];
      options[optionIndex] = value;
      newQuestions[questionIndex] = { ...newQuestions[questionIndex], options };
      return newQuestions;
    });
  };

  const addOption = (questionIndex: number) => {
    setQuestions(prev => {
      const newQuestions = [...prev];
      const options = [...(newQuestions[questionIndex].options || []), 'Nová možnost'];
      newQuestions[questionIndex] = { ...newQuestions[questionIndex], options };
      return newQuestions;
    });
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    setQuestions(prev => {
      const newQuestions = [...prev];
      const options = (newQuestions[questionIndex].options || []).filter((_, i) => i !== optionIndex);
      newQuestions[questionIndex] = { ...newQuestions[questionIndex], options };
      return newQuestions;
    });
  };

  // Get section label
  const getSectionLabel = (section: string): string => {
    const found = sectionOptions.find(s => s.value === section);
    return found?.label || section;
  };

  // Get type label
  const getTypeLabel = (type: string): string => {
    const found = questionTypes.find(t => t.value === type);
    return found?.label || type;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
        <div className="text-center">
          <SpinnerIcon />
          <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>Načítám dotazník...</p>
        </div>
      </div>
    );
  }

  if (error && !survey) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--danger-100)' }}>
            <AlertIcon />
          </div>
          <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Chyba</h2>
          <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>{error}</p>
          <Link
            to="/survey"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium"
            style={{ background: 'var(--phoenix-500)', color: 'white' }}
          >
            <ArrowLeftIcon /> Zpět na dotazníky
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50"
        style={{
          background: 'linear-gradient(135deg, var(--phoenix-700) 0%, var(--phoenix-900) 100%)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
        }}
      >
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Back button */}
            <Link
              to="/survey"
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeftIcon />
              <span className="hidden sm:inline">Zpět</span>
            </Link>

            {/* Title */}
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white"
                style={{ background: 'rgba(255, 255, 255, 0.2)' }}
              >
                <LogoIcon />
              </div>
              <span className="font-semibold text-white">Úprava dotazníku</span>
            </div>

            {/* Save button */}
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: hasChanges ? 'white' : 'rgba(255,255,255,0.2)',
                color: hasChanges ? 'var(--phoenix-700)' : 'white'
              }}
            >
              {isSaving ? <SpinnerIcon /> : <SaveIcon />}
              <span className="hidden sm:inline">Uložit</span>
            </button>
          </div>
        </div>
      </header>

      {/* Success/Error messages */}
      {successMessage && (
        <div className="max-w-5xl mx-auto px-4 lg:px-8 pt-4">
          <div
            className="rounded-xl p-4 flex items-center gap-3"
            style={{ background: 'var(--success-100)', border: '1px solid var(--success-300)' }}
          >
            <span style={{ color: 'var(--success-600)' }}>✓</span>
            <p className="text-sm" style={{ color: 'var(--success-700)' }}>{successMessage}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="max-w-5xl mx-auto px-4 lg:px-8 pt-4">
          <div
            className="rounded-xl p-4 flex items-center gap-3"
            style={{ background: 'var(--danger-100)', border: '1px solid var(--danger-300)' }}
          >
            <AlertIcon />
            <p className="text-sm" style={{ color: 'var(--danger-700)' }}>{error}</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 lg:px-8 py-8">
        {/* Survey Info */}
        <div
          className="rounded-2xl p-6 mb-6"
          style={{ background: 'var(--bg-secondary)', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
        >
          <h2 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            Základní informace
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                Název dotazníku
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border transition-all focus:ring-2 focus:ring-blue-500"
                style={{ background: 'var(--bg-primary)', borderColor: 'var(--border-light)', color: 'var(--text-primary)' }}
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="w-5 h-5 rounded"
                />
                <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  Aktivní (přijímá odpovědi)
                </span>
              </label>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
              Popis
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-4 py-2 rounded-lg border transition-all focus:ring-2 focus:ring-blue-500"
              style={{ background: 'var(--bg-primary)', borderColor: 'var(--border-light)', color: 'var(--text-primary)' }}
            />
          </div>

          <div className="mt-4 flex items-center gap-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            <span>Obec: <strong style={{ color: 'var(--text-primary)' }}>{survey?.municipality_name}</strong></span>
            <span>•</span>
            <span>Otázek: <strong style={{ color: 'var(--text-primary)' }}>{questions.length}</strong></span>
            <span>•</span>
            <span>Odpovědí: <strong style={{ color: 'var(--text-primary)' }}>{survey?.response_count || 0}</strong></span>
          </div>
        </div>

        {/* Questions List */}
        <div
          className="rounded-2xl p-6"
          style={{ background: 'var(--bg-secondary)', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
              Otázky ({questions.length})
            </h2>
            <button
              onClick={addQuestion}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all hover:opacity-90"
              style={{ background: 'var(--phoenix-500)', color: 'white' }}
            >
              <PlusIcon />
              Přidat otázku
            </button>
          </div>

          {/* Questions */}
          <div className="space-y-3">
            {questions.map((question, index) => (
              <div
                key={question.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`rounded-xl border transition-all ${
                  draggedIndex === index ? 'opacity-50' : ''
                } ${expandedQuestion === question.id ? 'ring-2 ring-blue-500' : ''}`}
                style={{ background: 'var(--bg-primary)', borderColor: 'var(--border-light)' }}
              >
                {/* Question Header */}
                <div
                  className="flex items-center gap-3 p-4 cursor-pointer"
                  onClick={() => setExpandedQuestion(expandedQuestion === question.id ? null : question.id)}
                >
                  {/* Drag Handle */}
                  <span
                    className="cursor-move"
                    style={{ color: 'var(--text-tertiary)' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <GripIcon />
                  </span>

                  {/* Question Number */}
                  <span
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-semibold flex-shrink-0"
                    style={{ background: 'var(--phoenix-100)', color: 'var(--phoenix-700)' }}
                  >
                    {index + 1}
                  </span>

                  {/* Question Text */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                      {question.text}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                      {getSectionLabel(question.section)} • {getTypeLabel(question.type)}
                    </p>
                  </div>

                  {/* Move buttons */}
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => moveQuestion(index, index - 1)}
                      disabled={index === 0}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
                      style={{ color: 'var(--text-tertiary)' }}
                    >
                      <ChevronUpIcon />
                    </button>
                    <button
                      onClick={() => moveQuestion(index, index + 1)}
                      disabled={index === questions.length - 1}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
                      style={{ color: 'var(--text-tertiary)' }}
                    >
                      <ChevronDownIcon />
                    </button>
                  </div>

                  {/* Delete */}
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteQuestion(index); }}
                    className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                    style={{ color: 'var(--danger-500)' }}
                  >
                    <TrashIcon />
                  </button>
                </div>

                {/* Expanded Edit Form */}
                {expandedQuestion === question.id && (
                  <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--border-light)' }}>
                    <div className="pt-4 space-y-4">
                      {/* Question Text */}
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                          Text otázky
                        </label>
                        <textarea
                          value={question.text}
                          onChange={(e) => updateQuestion(index, { text: e.target.value })}
                          rows={2}
                          className="w-full px-4 py-2 rounded-lg border transition-all focus:ring-2 focus:ring-blue-500"
                          style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-light)', color: 'var(--text-primary)' }}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Question Type */}
                        <div>
                          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                            Typ otázky
                          </label>
                          <select
                            value={question.type}
                            onChange={(e) => {
                              const newType = e.target.value as any;
                              const updates: Partial<Question> = { type: newType };
                              // Initialize options for choice type
                              if (newType === 'choice' && !question.options) {
                                updates.options = ['Možnost 1', 'Možnost 2', 'Možnost 3'];
                              }
                              updateQuestion(index, updates);
                            }}
                            className="w-full px-4 py-2 rounded-lg border transition-all focus:ring-2 focus:ring-blue-500"
                            style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-light)', color: 'var(--text-primary)' }}
                          >
                            {questionTypes.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Section */}
                        <div>
                          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                            Sekce
                          </label>
                          <select
                            value={question.section}
                            onChange={(e) => updateQuestion(index, { section: e.target.value as any })}
                            className="w-full px-4 py-2 rounded-lg border transition-all focus:ring-2 focus:ring-blue-500"
                            style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-light)', color: 'var(--text-primary)' }}
                          >
                            {sectionOptions.map(s => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Required */}
                        <div className="flex items-center">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={question.required}
                              onChange={(e) => updateQuestion(index, { required: e.target.checked })}
                              className="w-5 h-5 rounded"
                            />
                            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                              Povinná
                            </span>
                          </label>
                        </div>
                      </div>

                      {/* Options for choice type */}
                      {question.type === 'choice' && (
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                            Možnosti odpovědí
                          </label>
                          <div className="space-y-2">
                            {(question.options || []).map((option, optIndex) => (
                              <div key={optIndex} className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={option}
                                  onChange={(e) => updateOption(index, optIndex, e.target.value)}
                                  className="flex-1 px-3 py-2 rounded-lg border transition-all focus:ring-2 focus:ring-blue-500"
                                  style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-light)', color: 'var(--text-primary)' }}
                                />
                                <button
                                  onClick={() => removeOption(index, optIndex)}
                                  className="p-2 rounded-lg hover:bg-red-50"
                                  style={{ color: 'var(--danger-500)' }}
                                >
                                  <TrashIcon />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => addOption(index)}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all hover:bg-gray-100 dark:hover:bg-gray-700"
                              style={{ color: 'var(--phoenix-600)' }}
                            >
                              <PlusIcon />
                              Přidat možnost
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {questions.length === 0 && (
            <div className="text-center py-12" style={{ color: 'var(--text-tertiary)' }}>
              <p className="mb-4">Dotazník nemá žádné otázky.</p>
              <button
                onClick={addQuestion}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium"
                style={{ background: 'var(--phoenix-500)', color: 'white' }}
              >
                <PlusIcon />
                Přidat první otázku
              </button>
            </div>
          )}
        </div>

        {/* Save Button (bottom) */}
        {hasChanges && (
          <div className="sticky bottom-4 mt-6">
            <div
              className="rounded-xl p-4 flex items-center justify-between"
              style={{
                background: 'var(--phoenix-500)',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
              }}
            >
              <p className="text-white font-medium">
                Máte neuložené změny
              </p>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-6 py-2 rounded-lg font-semibold transition-all bg-white hover:bg-gray-100"
                style={{ color: 'var(--phoenix-700)' }}
              >
                {isSaving ? <SpinnerIcon /> : <SaveIcon />}
                Uložit změny
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SurveyEditor;
